<template>
    <div class="main-content">
        <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 1060">
            <transition-group name="slide-fade">
                <div v-for="alert in alerts" :key="alert.id"
                    class="toast show align-items-center border-0 shadow-lg mb-2"
                    :class="'bg-' + alert.type + '-subtle text-' + alert.type" role="alert">
                    <div class="d-flex p-3">
                        <i :class="alert.icon" class="me-3 fs-5"></i>
                        <div class="toast-body p-0 fw-medium">
                            {{ alert.message }}
                        </div>
                        <button type="button" class="btn-close ms-auto" @click="removeAlert(alert.id)"></button>
                    </div>
                </div>
            </transition-group>
        </div>

        <header class="content-header mb-2 mt-n5">
            <nav aria-label="breadcrumb">
                <ol class="breadcrumb mb-0">
                    <li class="breadcrumb-item"><router-link class="nav-link active" to="/">Início</router-link></li>
                    <li class="breadcrumb-item active text-dark small">Configurações do Sistema</li>
                </ol>
            </nav>
        </header>

        <div class="card p-4 shadow-subtle rounded-4 border-0">
            <div class="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
                <div>
                    <h4 class="mb-1 fw-bold">Configurações do Sistema</h4>
                    <span class="text-muted fs-7">Ajuste os parâmetros gerais e regras de negócio da aplicação.</span>
                </div>
                <div class="d-flex gap-2 align-items-center" v-if="['cobrancas', 'notificacoes'].includes(activeTab)">
                    <button class="btn btn-pmg-primary d-inline-flex align-items-center" @click="saveSettings"
                        :disabled="loading">
                        <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"
                            aria-hidden="true"></span>
                        <i v-else class="bi bi-floppy me-2"></i>
                        {{ loading ? 'Salvando...' : 'Salvar Alterações' }}
                    </button>
                </div>
            </div>

            <div v-if="!initialLoading">
                <ul class="nav nav-tabs custom-tabs mb-4 border-bottom">
                    <li class="nav-item">
                        <a class="nav-link" :class="{ active: activeTab === 'cobrancas' }"
                            @click.prevent="activeTab = 'cobrancas'" href="#">
                            <i class="bi bi-cash-coin me-2"></i>Cobranças
                        </a>
                    </li>
                    <li class="nav-item">
                        <a class="nav-link" :class="{ active: activeTab === 'notificacoes' }"
                            @click.prevent="activeTab = 'notificacoes'" href="#">
                            <i class="bi bi-bell me-2"></i>Notificações
                        </a>
                    </li>
                    <!-- NOVA ABA DE GATEWAYS -->
                    <li class="nav-item">
                        <a class="nav-link" :class="{ active: activeTab === 'gateways' }"
                            @click.prevent="activeTab = 'gateways'" href="#">
                            <i class="bi bi-hdd-network me-2"></i>Gateways de Pagamento
                        </a>
                    </li>
                </ul>

                <!-- Aba: Cobranças -->
                <div v-if="activeTab === 'cobrancas'" class="row g-4 slide-in">
                    <div class="col-12">
                        <h6 class="fw-bold text-dark mb-3"><i class="bi bi-receipt me-2 text-primary"></i>Regras Padrão
                            de Atraso</h6>
                        <div class="row g-3">
                            <div class="col-md-3">
                                <label class="form-label small fw-bold text-muted">Tipo de Multa</label>
                                <select v-model="form.fine_type" class="form-select form-control-pmg">
                                    <option value="percentage">Percentual (%)</option>
                                    <option value="fixed">Fixo (R$)</option>
                                </select>
                            </div>
                            <div class="col-md-3">
                                <label class="form-label small fw-bold text-muted">Multa por Atraso</label>
                                <div class="input-group shadow-sm">
                                    <span v-if="form.fine_type === 'fixed'"
                                        class="input-group-text bg-white text-muted fw-bold border-end-0">R$</span>
                                    <input v-if="form.fine_type === 'percentage'" type="number" step="0.01" min="0"
                                        v-model.number="form.fine_value" class="form-control border-end-0 bg-white"
                                        placeholder="Ex: 2.00">
                                    <input v-else type="text" class="form-control bg-white"
                                        :class="{ 'border-start-0': form.fine_type === 'fixed' }"
                                        :value="formatCurrencyInput(form.fine_value)"
                                        @input="handleCurrencyInput($event, 'fine_value')" placeholder="0,00">
                                    <span v-if="form.fine_type === 'percentage'"
                                        class="input-group-text bg-white text-muted fw-bold">%</span>
                                </div>
                                <div class="form-text text-muted small mt-1" style="font-size: 0.7rem;">
                                    Cobrada uma única vez.
                                </div>
                            </div>
                            <div class="col-md-3">
                                <label class="form-label small fw-bold text-muted">Juros ao Mês (%)</label>
                                <div class="input-group shadow-sm">
                                    <input type="number" step="0.01" min="0" v-model.number="form.interest_percentage"
                                        class="form-control border-end-0 bg-white" placeholder="Ex: 1.00">
                                    <span class="input-group-text bg-white text-muted fw-bold">%</span>
                                </div>
                                <div class="form-text text-muted small mt-1" style="font-size: 0.7rem;">
                                    Pró-rata mês (Padrão: 1% a.m).
                                </div>
                            </div>
                            <div class="col-md-3">
                                <label class="form-label small fw-bold text-muted">Dias de tolerância</label>
                                <div class="input-group shadow-sm">
                                    <input type="number" step="0.01" min="0" v-model.number="form.tolerance_days"
                                        class="form-control border-end-0 bg-white" placeholder="Ex: 1.00">
                                    <span class="input-group-text bg-white text-muted fw-bold">%</span>
                                </div>
                                <div class="form-text text-muted small mt-1" style="font-size: 0.7rem;">
                                    Quantidade de dias antes do status alterar para atrasado
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-12">
                        <hr class="text-muted opacity-25">
                    </div>
                    <div class="col-12">
                        <h6 class="fw-bold text-dark mb-3"><i class="bi bi-gift me-2 text-primary"></i>Regras Padrão de
                            Bonificação</h6>
                        <div class="row g-3">
                            <div class="col-md-4">
                                <label class="form-label small fw-bold text-muted">Tipo de Bonificação</label>
                                <select v-model="form.reward_type" class="form-select form-control-pmg">
                                    <option value="discount">Desconto</option>
                                    <option value="extension">Extensão de Prazo</option>
                                </select>
                            </div>
                            <div class="col-md-4">
                                <label class="form-label small fw-bold text-muted">Porcentagem (%)</label>
                                <div class="input-group shadow-sm">
                                    <input type="text" class="form-control border-end-0 bg-white"
                                        :value="formatCurrencyInput(form.reward_percentage)"
                                        @input="handlePercentageInput($event, 'reward_percentage')" placeholder="0,00">
                                    <span class="input-group-text bg-white text-muted fw-bold">%</span>
                                </div>
                                <div class="form-text text-muted small mt-1" style="font-size: 0.7rem;">
                                    Aplicado sobre o valor (0 a 100).
                                </div>
                            </div>
                            <div class="col-md-4">
                                <label class="form-label small fw-bold text-muted">Fração Mínima (R$)</label>
                                <div class="input-group shadow-sm">
                                    <span class="input-group-text bg-white text-muted fw-bold border-end-0">R$</span>
                                    <input type="text" class="form-control bg-white border-start-0"
                                        :value="formatCurrencyInput(form.fractional_min_amount)"
                                        @input="handleCurrencyInput($event, 'fractional_min_amount')"
                                        placeholder="0,00">
                                </div>
                                <div class="form-text text-muted small mt-1" style="font-size: 0.7rem;">
                                    Valor mínimo pago para acionar a bonificação.
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-12">
                        <hr class="text-muted opacity-25">
                    </div>
                    <div class="col-12">
                        <h6 class="fw-bold text-dark mb-3"><i class="bi bi-bank me-2 text-primary"></i>Gateway de
                            Pagamento Padrão</h6>
                        <div class="row g-3">
                            <div class="col-md-6">
                                <label class="form-label small fw-bold text-muted">Provedor Principal</label>
                                <select v-model="form.payment_gateway" class="form-select form-control-pmg"
                                    :disabled="loadingGateways">
                                    <option value="">Sem gateway padrão</option>
                                    <option v-for="gateway in gateways" :key="gateway.id" :value="gateway.id">
                                        {{ gateway.name }}
                                    </option>
                                </select>
                                <div v-if="loadingGateways" class="form-text text-muted small mt-1">
                                    <span class="spinner-border spinner-border-sm me-1" role="status"
                                        aria-hidden="true"></span>
                                    Carregando gateways...
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Aba: Notificações -->
                <div v-if="activeTab === 'notificacoes'" class="row g-4 slide-in">
                    <div class="col-12">
                        <h6 class="fw-bold text-dark mb-3"><i class="bi bi-receipt me-2 text-primary"></i>Notificar por
                            e-mail</h6>
                        <div class="row g-3">
                            <div class="col-md-3">
                                <label class="form-label small fw-bold text-muted">Ao criar nova transação</label>
                                <select v-model="form.email_new_revenue" class="form-select form-control-pmg">
                                    <option value="1">Sim</option>
                                    <option value="0">Não</option>
                                </select>
                            </div>
                            <div class="col-md-3">
                                <label class="form-label small fw-bold text-muted">Transação em atraso</label>
                                <select v-model="form.email_payment_overdue" class="form-select form-control-pmg">
                                    <option value="1">Sim</option>
                                    <option value="0">Não</option>
                                </select>
                            </div>
                            <div class="col-md-3">
                                <label class="form-label small fw-bold text-muted">Transação liquidada</label>
                                <select v-model="form.email_payment_received" class="form-select form-control-pmg">
                                    <option value="1">Sim</option>
                                    <option value="0">Não</option>
                                </select>
                            </div>
                            <div class="col-md-3">
                                <label class="form-label small fw-bold text-muted">Código de acesso ao portal do
                                    cliente</label>
                                <select v-model="form.email_code_portal_client" class="form-select form-control-pmg">
                                    <option value="1">Sim</option>
                                    <option value="0">Não</option>
                                </select>
                            </div>
                        </div>
                        <div class="row mt-2">
                            <div class="col-md-3">
                                <label class="form-label small fw-bold text-muted">Protocolo de atendimento CRM</label>
                                <select v-model="form.email_protocol_crm" class="form-select form-control-pmg">
                                    <option value="1">Sim</option>
                                    <option value="0">Não</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- ABA: GATEWAYS DE PAGAMENTO -->
                <div v-if="activeTab === 'gateways'" class="slide-in">
                    <!-- O componente de listagem será injetado aqui -->
                    <ListGateway />
                </div>

            </div>
            <div v-else class="text-center py-5 text-muted">
                <div class="spinner-border text-secondary mb-3" role="status"></div>
                <div>Carregando configurações...</div>
            </div>

        </div>
    </div>
</template>

<script>
import api from '@/services/api';
import ListGateway from '../gateway/list-gateway.vue';

export default {
    inject: ['globalLoading'],
    components: {
        ListGateway
    },
    data() {
        return {
            alerts: [],
            loading: false,
            initialLoading: true,
            activeTab: 'cobrancas',

            form: {
                active_ai_driver: 'gemini',
                gemini_model: '',
                active_storage_driver: 'google',
                google_drive_folder_id: '',
                ai_verification_enabled: '1',
                fine_type: 'percentage',
                fine_value: '',
                interest_percentage: '',
                tolerance_days: '',
                payment_gateway: '',
                reward_type: 'extension',
                reward_percentage: '1.00',
                fractional_min_amount: '10.00',
                email_new_revenue: '1',
                email_payment_overdue: '1',
                email_payment_received: '1',
                email_code_portal_client: '1',
                email_protocol_crm:'1'
            },
            gateways: [], // Para popular o select da aba de Cobranças
            loadingGateways: false,
        }
    },
    mounted() {
        this.fetchSettings();
        this.fetchGateways();
    },
    methods: {
        async fetchSettings() {
            this.initialLoading = true;
            try {
                const response = await api.get('/system-settings');
                const settingsData = response.data.data || response.data;

                settingsData.forEach(setting => {
                    if (this.form[setting.key] !== undefined) {
                        this.form[setting.key] = setting.value || '';
                    }
                });
            } catch (error) {
                this.showAlert('Falha ao carregar as configurações do servidor.', 'error');
            } finally {
                this.initialLoading = false;
            }
        },
        async fetchGateways() {
            this.loadingGateways = true;
            try {
                const response = await api.get('/payment-gateways');
                this.gateways = response.data.data || [];
            } catch (error) {
                this.gateways = [];
            } finally {
                this.loadingGateways = false;
            }
        },

        async saveSettings() {
            if (parseFloat(this.form.reward_percentage) > 100) {
                this.showAlert('A porcentagem de bonificação não pode ser maior que 100%.', 'warning');
                return;
            }
            if (parseFloat(this.form.interest_percentage) > 100) {
                this.showAlert('A taxa de juros ao mês não pode ultrapassar 100%.', 'warning');
                return;
            }
            if (this.form.fine_type === 'percentage' && parseFloat(this.form.fine_value) > 100) {
                this.showAlert('A multa por atraso em porcentagem não pode ser maior que 100%.', 'warning');
                return;
            }

            this.loading = true;

            const payload = {
                settings: Object.keys(this.form).map(key => ({
                    key: key,
                    value: this.form[key] === '' || this.form[key] === null ? null : String(this.form[key])
                }))
            };

            try {
                this.globalLoading.show('Salvando configurações...');
                await api.put('/system-settings', payload);
                this.showAlert("Configurações atualizadas com sucesso!");
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Ocorreu um erro ao salvar as configurações.';
                this.showAlert(errorMessage, 'error');
            } finally {
                this.globalLoading.hide();
                this.loading = false;
            }
        },

        formatCurrencyInput(value) {
            if (!value || isNaN(value)) return '';
            let val = Number(value).toFixed(2).replace('.', ',');
            return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
        },

        handleCurrencyInput(event, field) {
            let val = event.target.value.replace(/\D/g, '');
            if (val === '') {
                this.form[field] = null;
                return;
            }
            this.form[field] = (parseInt(val) / 100).toFixed(2);
            this.$forceUpdate();
        },

        showAlert(message, type = 'success') {
            const id = Date.now();
            let icon = 'bi-check-circle-fill';

            if (type === 'danger') icon = 'bi-exclamation-circle-fill';
            if (type === 'warning') icon = 'bi-exclamation-triangle-fill';
            if (type === 'error') icon = 'bi-x-circle-fill';

            this.alerts.push({ id, message, type, icon });
            const timeout = type === 'error' ? 6000 : 4000;
            setTimeout(() => this.removeAlert(id), timeout);
        },

        removeAlert(id) {
            this.alerts = this.alerts.filter(a => a.id !== id);
        },
        handlePercentageInput(event, field) {
            let val = event.target.value.replace(/\D/g, '');

            if (val === '') {
                this.form[field] = null;
                return;
            }

            let floatVal = parseFloat(val) / 100;
            if (floatVal > 100) {
                floatVal = 100.00;
            }

            this.form[field] = floatVal.toFixed(2);
            this.$forceUpdate();
        },
    }
}
</script>

<style scoped>
/* TODO ESTILO DE system-settings PERMANECE EXATAMENTE IGUAL */
.toast {
    min-width: 300px;
    border-radius: 12px;
    background-color: white !important;
}

.bg-success-subtle {
    background-color: #ecfdf5 !important;
    color: #059669 !important;
    border-left: 5px solid #059669 !important;
}

.bg-error-subtle {
    background-color: #fef2f2 !important;
    color: #991b1b !important;
    border-left: 5px solid #ef4444 !important;
    animation: shake 0.4s ease-in-out;
}

.main-content {
    background-color: #f8fafc;
    min-height: 100vh;
    padding: 2rem;
    font-family: 'Inter', sans-serif;
}

.card {
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.02);
}

.btn-pmg-primary {
    background: #561569;
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 10px 20px;
    font-weight: 500;
    transition: all 0.2s ease;
}

.btn-pmg-primary:hover:not(:disabled) {
    background: #19274a;
}

.btn-pmg-primary:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.form-control-pmg {
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    padding: 12px;
    font-size: 0.95rem;
    color: #334155;
}

.form-control-pmg:focus,
.form-control:focus {
    border-color: #561569;
    box-shadow: 0 0 0 4px rgba(33, 51, 97, 0.1);
    outline: none;
    z-index: 2;
}

select.form-control-pmg {
    appearance: form-select;
    cursor: pointer;
}

.input-group .form-control {
    font-size: 0.95rem;
    color: #334155;
}

.input-group-text {
    font-size: 0.95rem;
}

.custom-tabs {
    border-bottom: 1px solid #e2e8f0;
}

.custom-tabs .nav-link {
    color: #64748b;
    border: none;
    border-bottom: 2px solid transparent;
    font-weight: 500;
    padding: 12px 24px;
    margin-bottom: -1px;
    transition: all 0.2s ease;
}

.custom-tabs .nav-link:hover {
    color: #561569;
    border-bottom-color: #cbd5e1;
}

.custom-tabs .nav-link.active {
    color: #561569;
    background-color: transparent;
    border-bottom: 2px solid #561569;
}

.slide-in {
    animation: fadeSlide 0.3s ease-out;
}

@keyframes fadeSlide {
    from {
        opacity: 0;
        transform: translateY(5px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: all 0.4s ease;
}

.slide-fade-enter-from {
    transform: translateX(100px);
    opacity: 0;
}

.slide-fade-leave-to {
    opacity: 0;
    transform: scale(0.9);
}

@keyframes shake {

    0%,
    100% {
        transform: translateX(0);
    }

    25% {
        transform: translateX(-5px);
    }

    75% {
        transform: translateX(5px);
    }
}
</style>